import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ModuleFrame, { Chip } from '../../components/ModuleFrame';
import PreflightPanel from '../../components/training/PreflightPanel';
import { friendlyError, updateEntity, useEntityList } from '../../data/entities';
import { cancelJob, jobIsActive, queueFanoutJob, useJob } from '../../data/firestore';
import { agentOsProblems } from '../../data/agentosGraph';

// Neural Net Training Center — the nQube tab that watches a training
// pipeline the way an engineer watches a launch: the stage rail
// (prepare → features → sequences → pre-flight → approve → train →
// evaluate), the pre-flight checklist as it verifies, the approval that
// gates the GPU spend, the live loss curve, the held-out result, and what
// it cost. Everything here is read from the run's job doc
// (jobs/{id}.liveBlocks + output.outputs) and the pipeline's graph; the
// only writes are Run (queue the job) and Cancel (the jobControls kill
// switch). A training pipeline is any pipeline with a control.preflight
// block — the seeded example is ex-nn-training.

const STAGE_LABEL = { prepare: 'Prepare', features: 'Features', sequences: 'Sequences', preflight: 'Pre-flight', train: 'Train', evaluate: 'Evaluate' };
const fmtUsd = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : `$${Number(v).toFixed(2)}`);
const fmtMin = (h) => (h == null ? '—' : Number(h) * 60 < 1 ? '<1 min' : Number(h) * 60 < 90 ? `${Math.round(Number(h) * 60)} min` : `${Number(h).toFixed(1)} h`);
const fmtDur = (ms) => (!ms ? '' : ms < 90000 ? `${Math.round(ms / 1000)}s` : `${Math.round(ms / 60000)} min`);
const f4 = (v) => (Number.isFinite(Number(v)) && v != null ? Number(v).toFixed(4) : '—');

function stageOf(block) {
  if (!block) return null;
  if (block.type === 'control.preflight') return 'preflight';
  if (block.type === 'container.run') return String(block.config?.stage || '').trim() || 'container';
  if (block.type === 'control.review') return 'approve';
  if (block.type === 'input.text') return 'config';
  if (block.type === 'output.knowledge') return 'report';
  if (block.type === 'transform.llm') return 'explain';
  return block.type;
}

function parseConfig(text) {
  try { return JSON.parse(String(text || '')); } catch { return null; }
}

export default function TrainingCenter({ org }) {
  const { docs: workspaces } = useEntityList('workspace', { org: org.id });
  const [byWs, setByWs] = useState({});
  const onDocs = useCallback((wsId, docs) => setByWs((m) => ({ ...m, [wsId]: docs })), []);
  const trainingPipelines = useMemo(() => Object.entries(byWs)
    .flatMap(([wsId, docs]) => docs.map((p) => ({ ...p, wsId })))
    .filter((p) => (p.graph?.blocks || []).some((b) => b.type === 'control.preflight'))
    .sort((a, b) => (a.id === 'ex-nn-training' ? -1 : b.id === 'ex-nn-training' ? 1 : (b.updatedAt || 0) - (a.updatedAt || 0))), [byWs]);
  const [pickedId, setPickedId] = useState('');
  const pipeline = trainingPipelines.find((p) => `${p.wsId}/${p.id}` === pickedId) || trainingPipelines[0] || null;
  const scope = useMemo(() => (pipeline ? { org: org.id, workspace: pipeline.wsId } : null), [org.id, pipeline]);
  const { job } = useJob(pipeline?.lastRunJobId || null);
  const { docs: reviewItems } = useEntityList('reviewItem', { org: org.id });
  const { docs: integrations } = useEntityList('integration', { org: org.id });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const blocks = useMemo(() => [...(pipeline?.graph?.blocks || [])].sort((a, b) => (a.position?.x || 0) - (b.position?.x || 0) || (a.position?.y || 0) - (b.position?.y || 0)), [pipeline]);
  const live = job?.liveBlocks || {};
  const outputs = job?.output?.outputs || {};
  const active = jobIsActive(job);
  const find = (stage) => blocks.find((b) => stageOf(b) === stage);
  const preflightBlock = find('preflight');
  const trainBlock = find('train');
  const evalBlock = find('evaluate');
  const approveBlock = find('approve');
  const configBlock = find('config');
  const cfg = parseConfig(configBlock?.config?.text) || {};
  const budgetUsd = Number(preflightBlock?.config?.budgetUsd) || cfg.max_cost_usd || null;

  const preflightLive = live[preflightBlock?.id];
  const preflightOut = outputs[preflightBlock?.id];
  const report = (preflightOut && preflightOut.checks && preflightOut) || preflightLive?.progress || null;
  const preflightRunning = preflightLive?.status === 'running';
  const trainLive = live[trainBlock?.id];
  const trainProgress = trainLive?.progress || null;
  const trainOut = outputs[trainBlock?.id];
  const evalOut = outputs[evalBlock?.id];
  const pendingReview = (reviewItems || []).find((r) => r.status === 'pending' && r.source?.runId === job?.id);
  const approveLive = live[approveBlock?.id];
  const gpuHourUsd = Number(report?.config?.gpu_hour_usd) || 1.2;
  const actualGpuHours = Number(trainOut?.gpu_hours) || (trainProgress?.device === 'cuda' && trainProgress?.elapsedSec ? trainProgress.elapsedSec / 3600 : null);
  const verdict = report?.verdict || (preflightLive?.port === 'nogo' ? 'NO-GO' : preflightLive?.status === 'done' ? 'GO' : null);

  useEffect(() => { setErr(''); }, [pipeline?.id]);

  const run = async () => {
    if (!pipeline || !scope) return;
    setErr(''); setBusy(true);
    try {
      const problems = agentOsProblems(pipeline.graph, pipeline.capabilities || [], { env: pipeline.env || 'sandbox', integrations: integrations || null });
      if (problems.length) throw new Error(problems.join(' '));
      const jobId = await queueFanoutJob('pipeline', { orgId: org.id, workspaceId: pipeline.wsId, pipelineId: pipeline.id }, { title: `training: ${pipeline.name || pipeline.id}` });
      await updateEntity('pipeline', scope, pipeline.id, { lastRunJobId: jobId });
    } catch (e) { setErr(friendlyError(e)); } finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!job) return;
    setBusy(true);
    try { await cancelJob(job.id); } catch (e) { setErr(friendlyError(e)); } finally { setBusy(false); }
  };

  const canvasHref = pipeline ? `/orgs/${org.id}/ws/${pipeline.wsId}/pipelines/${pipeline.id}` : null;
  const runState = !job ? 'idle' : active ? (pendingReview ? 'awaiting approval' : 'running') : job.status === 'done' ? 'done' : job.status === 'cancelled' ? 'cancelled' : job.status === 'error' ? 'failed' : job.status;

  return (
    <div className="tc" data-nova-note={pipeline ? `Training Center: ${pipeline.name}, run ${runState}${verdict ? `, pre-flight ${verdict}` : ''}` : 'Training Center: no training pipeline in this org'}>
      {workspaces?.map((ws) => <Collector key={ws.id} orgId={org.id} wsId={ws.id} onDocs={onDocs} />)}

      {!pipeline && (
        <ModuleFrame.Panel title="Neural Net Training Center" meta="train a model with a pre-flight in front of every GPU hour">
          <p className="cs-help">No training pipeline here yet. A training pipeline is any workflow with a <strong>Pre-flight check</strong> block in front of its GPU stage — seed the examples (<code>scripts/seed-example-pipelines.mjs</code>) or add the block to a workflow on the canvas.</p>
        </ModuleFrame.Panel>
      )}

      {pipeline && (
        <>
          <ModuleFrame.Panel
            title="Neural Net Training Center"
            meta={<>{pipeline.name} · {cfg.task || 'regression'} · window {cfg.window ?? '—'} · horizon {cfg.horizon ?? '—'} · {cfg.epochs ?? '—'} epochs · batch {cfg.batch_size ?? '—'} · lr {cfg.lr ?? '—'}{cfg.dataset?.kind === 'sample' ? ' · synthetic sample dataset' : ''}</>}
            actions={(
              <>
                {trainingPipelines.length > 1 && (
                  <select className="cs-input" value={pickedId || `${pipeline.wsId}/${pipeline.id}`} onChange={(e) => setPickedId(e.target.value)} aria-label="Training pipeline">
                    {trainingPipelines.map((p) => <option key={`${p.wsId}/${p.id}`} value={`${p.wsId}/${p.id}`}>{p.name}</option>)}
                  </select>
                )}
                {canvasHref && <Link className="cs-btn cs-btn-sm cs-btn-ghost" to={canvasHref}>Open canvas</Link>}
                {active
                  ? <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" data-agent-guard disabled={busy} onClick={cancel}>Cancel run</button>
                  : <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" data-agent-guard disabled={busy} onClick={run}>Run training</button>}
              </>
            )}
          >
            <ol className="tc-rail" aria-label="Training stages">
              {blocks.map((b) => {
                const st = stageOf(b);
                const l = live[b.id];
                const status = l?.status || (job && !active ? 'idle' : 'idle');
                const tone = l?.gateClosed ? 'skipped' : status;
                const sub = st === 'preflight' && verdict ? verdict
                  : st === 'train' && trainProgress ? `epoch ${trainProgress.epoch}/${trainProgress.epochs}`
                    : st === 'approve' && pendingReview ? 'awaiting a person'
                      : l?.port ? `→ ${l.port}` : l?.status === 'done' ? fmtDur(l.ms) : l?.status === 'running' ? 'running' : '';
                return (
                  <li key={b.id} className={`tc-stage is-${tone}${b.config?.gpu === 'true' ? ' is-gpu' : ''}`} title={l?.error || b.label}>
                    <span className={`nq-light nq-light-${tone === 'idle' ? 'queued' : tone}`} aria-hidden="true" />
                    <span className="tc-stage-name">{STAGE_LABEL[st] || b.label}</span>
                    {b.config?.gpu === 'true' && <span className="tc-gpu">GPU</span>}
                    {sub && <span className="tc-stage-sub">{sub}</span>}
                  </li>
                );
              })}
            </ol>
            <div className="tc-strip">
              <Stat k="Run" v={runState} tone={runState === 'failed' ? 'danger' : runState === 'done' ? 'ok' : runState === 'awaiting approval' ? 'warn' : null} />
              <Stat k="Budget" v={fmtUsd(budgetUsd)} />
              <Stat k="Estimate" v={report?.estimatedUsd != null ? `${fmtUsd(report.estimatedUsd)} · ${fmtMin(report.estimatedHours)}` : '—'} tone={report?.estimatedUsd != null && budgetUsd && Number(report.estimatedUsd) > budgetUsd ? 'danger' : null} />
              <Stat k="GPU so far" v={actualGpuHours != null ? `${fmtUsd(actualGpuHours * gpuHourUsd)} · ${fmtMin(actualGpuHours)}` : '—'} />
              <Stat k="Checks" v={report ? `${report.totalPassed || 0} pass · ${report.totalWarnings || 0} warn · ${report.totalFailed || 0} fail` : '—'} />
            </div>
            {err && <p className="cs-form-error" role="alert">{err}</p>}
            {job?.status === 'error' && job.error && <p className="cs-form-error" role="alert">{job.error}</p>}
            {pendingReview && (
              <div className="tc-approve" role="status" data-nova-note="The pre-flight passed; a person must approve the GPU spend in Human Review">
                <strong>Pre-flight passed — the GPU is waiting on a person.</strong>
                <span> {report?.estimatedUsd != null ? `Estimated ${fmtUsd(report.estimatedUsd)} for ${fmtMin(report.estimatedHours)}. ` : ''}Approve or reject in </span>
                <Link to={`/orgs/${org.id}/human-review`}>Human Review</Link>.
              </div>
            )}
            {approveLive?.status === 'done' && approveLive.port === 'reject' && <Chip tone="warn">GPU spend rejected — the run stopped at the gate</Chip>}
          </ModuleFrame.Panel>

          <div className="tc-grid">
            <PreflightPanel report={report} running={preflightRunning || (active && !report && !!preflightLive)} instant={!!preflightOut && !preflightRunning && !active} />
            <div className="tc-col">
              <TrainingCard progress={trainProgress} out={trainOut} live={trainLive} />
              <EvaluationCard out={evalOut} cfg={report?.config || cfg} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ k, v, tone }) {
  return (
    <div className={`mf-stat${tone ? ` is-${tone}` : ''}`}>
      <span className="mf-stat-v tc-stat-v">{v}</span>
      <span className="mf-stat-k">{k}</span>
    </div>
  );
}

function Collector({ orgId, wsId, onDocs }) {
  const { docs } = useEntityList('pipeline', { org: orgId, workspace: wsId });
  useEffect(() => { onDocs(wsId, docs || []); }, [wsId, docs, onDocs]);
  return null;
}

function LossChart({ history, width = 420, height = 120 }) {
  const pts = (history || []).filter((h) => Number.isFinite(h.loss));
  if (pts.length < 2) return <p className="mf-muted">Loss curve appears after the second epoch.</p>;
  const all = pts.flatMap((h) => [h.loss, h.valLoss].filter(Number.isFinite));
  const max = Math.max(...all); const min = Math.min(...all);
  const x = (i) => 8 + (i / (pts.length - 1)) * (width - 16);
  const y = (v) => 8 + (1 - (v - min) / Math.max(max - min, 1e-9)) * (height - 16);
  const path = (k) => pts.map((h, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(h[k]).toFixed(1)}`).join(' ');
  return (
    <svg className="tc-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Loss ${pts[0].loss.toFixed(3)} to ${pts[pts.length - 1].loss.toFixed(3)} over ${pts.length} epochs`}>
      <path d={path('loss')} className="tc-line-train" />
      <path d={path('valLoss')} className="tc-line-val" />
      {pts.map((h, i) => <circle key={i} cx={x(i)} cy={y(h.valLoss)} r="2.5" className="tc-dot-val" />)}
    </svg>
  );
}

function TrainingCard({ progress, out, live }) {
  const history = out?.history || progress?.history || [];
  const running = live?.status === 'running';
  const pct = progress?.pct ?? (out ? 100 : 0);
  const title = running ? `Training · epoch ${progress?.epoch ?? 0}/${progress?.epochs ?? '?'}` : out ? 'Training · done' : 'Training';
  const meta = progress
    ? `${progress.device || ''}${progress.amp ? ' · fp16' : ''}${progress.gpuMemMb ? ` · ${Math.round(progress.gpuMemMb)} MB VRAM` : ''}${progress.etaSec != null && running ? ` · ~${Math.max(1, Math.round(progress.etaSec / 60))} min left` : ''}`
    : 'waits for the approved pre-flight';
  return (
    <ModuleFrame.Panel title={title} meta={meta} className="tc-card">
      {(running || out) && (
        <div className="pf-progress" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
          <div className="pf-progress-fill is-verifying" style={{ width: `${pct}%` }} />
        </div>
      )}
      <LossChart history={history} />
      {(progress || out) && (
        <div className="tc-kv">
          <span>train loss <strong>{f4(progress?.loss ?? out?.final_train_loss)}</strong></span>
          <span>val loss <strong>{f4(progress?.valLoss ?? out?.best_val_loss)}</strong></span>
          <span>best <strong>{f4(progress?.bestValLoss ?? out?.best_val_loss)}</strong> at epoch {progress?.bestEpoch ?? out?.best_epoch ?? '—'}</span>
          {out?.params && <span>{Number(out.params).toLocaleString()} params</span>}
        </div>
      )}
      {!progress && !out && <p className="cs-help">The train stage re-uploads its best checkpoint after every improving epoch, so a cancel still leaves a model behind.</p>}
    </ModuleFrame.Panel>
  );
}

function EvaluationCard({ out, cfg }) {
  if (!out || !out.metrics) {
    return (
      <ModuleFrame.Panel title="Evaluation" meta="held-out windows against the best checkpoint" className="tc-card">
        <p className="cs-help">Runs after training on the test split the model never saw; reports error in the target's own units next to a naive baseline, so the number means something.</p>
      </ModuleFrame.Panel>
    );
  }
  const m = out.metrics; const b = out.baseline || {};
  const cls = cfg?.task === 'classification';
  return (
    <ModuleFrame.Panel title="Evaluation" meta={`${m.n?.toLocaleString?.() || m.n} held-out windows · best epoch ${out.best?.epoch} of ${out.epochs}`} className="tc-card">
      <div className="mf-stats" data-nova-note={out.text}>
        {cls ? (
          <>
            <Stat k="Accuracy" v={m.accuracy} tone="ok" />
            <Stat k="Majority baseline" v={m.majority_baseline_accuracy} />
          </>
        ) : (
          <>
            <Stat k="MAE" v={m.mae} tone={out.improvement_pct > 0 ? 'ok' : 'warn'} />
            <Stat k="Baseline MAE" v={b.mae} />
            <Stat k="vs baseline" v={out.improvement_pct != null ? `${out.improvement_pct > 0 ? '+' : ''}${out.improvement_pct}%` : '—'} />
            <Stat k="R²" v={m.r2} />
          </>
        )}
      </div>
      {Array.isArray(out.feature_importance) && out.feature_importance.length > 0 && (
        <ul className="tc-features" aria-label="Feature importance">
          {out.feature_importance.slice(0, 6).map((f) => (
            <li key={f.name}><span className="tc-feature-name">{f.name}</span><span className="tc-feature-bar"><span style={{ width: `${Math.min(100, f.weight * 400)}%` }} /></span><span className="mf-muted">{f.weight.toFixed(2)}</span></li>
          ))}
        </ul>
      )}
      {out.text && <p className="cs-help">{out.text}</p>}
    </ModuleFrame.Panel>
  );
}
